import { ProgressBar, useDeviceType } from '@cian/ui-kit';
import * as React from 'react';

import { DEFAULT_DESKTOP_DURATION, DEFAULT_MOBILE_DURATION } from './constants';
import { useApplicationContext } from '../../utils/applicationContext';

interface IProgressTimerContainerProps {
  setIsVisible(value: boolean): void;
}

export const ProgressTimerContainer: React.FC<IProgressTimerContainerProps> = ({ setIsVisible }) => {
  const deviceType = useDeviceType();
  const { config } = useApplicationContext();

  const [progress, setProgress] = React.useState(0);
  const startTime = React.useRef(Date.now());
  const animationFrame = React.useRef<number>(0);

  const duration = React.useMemo(() => {
    const mobileDuration = config.get<number>('cookieAgreement.Duration.Mobile') ?? DEFAULT_MOBILE_DURATION;
    const desktopDuration = config.get<number>('cookieAgreement.Duration.Desktop') ?? DEFAULT_DESKTOP_DURATION;

    return deviceType === 'phone' ? mobileDuration : desktopDuration;
  }, [config, deviceType]);

  const updateProgress = React.useCallback(() => {
    const elapsed = Date.now() - startTime.current;
    const newRemaining = Math.max(0, duration - elapsed);
    const newProgress = Math.min(100, (elapsed / duration) * 100);

    setProgress(newProgress);

    if (newRemaining > 0) {
      animationFrame.current = requestAnimationFrame(updateProgress);
    } else {
      setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }
  }, [duration, setIsVisible]);

  React.useEffect(() => {
    if (duration <= 0) {
      return;
    }

    startTime.current = Date.now();
    setProgress(0);
    setIsVisible(true);
    animationFrame.current = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrame.current);
    };
  }, [duration, updateProgress, setIsVisible]);

  return <ProgressBar value={progress} styleType="bar" valueColor="accent-main-primary" />;
};
